/**
 * A double-ended queue (Deque) implementation with O(1) amortized operations
 * for adding and removing elements from both ends.
 */
export class Deque<T> {
  #items: Map<number, T> = new Map()
  #head = 0
  #tail = 0

  constructor(initialItems?: Iterable<T>) {
    if (initialItems) {
      for (const item of initialItems) {
        this.pushBack(item)
      }
    }
  }

  /** Returns the number of elements in the deque */
  get size(): number {
    return this.#tail - this.#head
  }

  /** Returns true if the deque is empty */
  get isEmpty(): boolean {
    return this.size === 0
  }

  /** Adds an element to the front of the deque */
  pushFront(item: T): void {
    this.#head--
    this.#items.set(this.#head, item)
  }

  /** Adds multiple elements to the front of the deque (first item in array becomes front) */
  pushFrontMany(items: T[]): void {
    // Add in reverse order so first item in array ends up at front
    for (let i = items.length - 1; i >= 0; i--) {
      const item = items[i]
      if (item !== undefined) {
        this.#head--
        this.#items.set(this.#head, item)
      }
    }
  }

  /** Adds an element to the back of the deque */
  pushBack(item: T): void {
    this.#items.set(this.#tail, item)
    this.#tail++
  }

  /** Adds multiple elements to the back of the deque */
  pushBackMany(items: T[]): void {
    for (const item of items) {
      this.#items.set(this.#tail, item)
      this.#tail++
    }
  }

  /** Removes and returns the element at the front of the deque */
  popFront(): T | undefined {
    if (this.isEmpty) {
      return undefined
    }
    const item = this.#items.get(this.#head)
    this.#items.delete(this.#head)
    this.#head++
    this.#rebalance()
    return item
  }

  /** Removes and returns up to `count` elements from the front of the deque */
  popFrontMany(count: number): T[] {
    const result: T[] = []
    const actualCount = Math.min(count, this.size)
    for (let i = 0; i < actualCount; i++) {
      result.push(this.#items.get(this.#head) as T)
      this.#items.delete(this.#head)
      this.#head++
    }
    this.#rebalance()
    return result
  }

  /** Removes and returns the element at the back of the deque */
  popBack(): T | undefined {
    if (this.isEmpty) {
      return undefined
    }
    this.#tail--
    const item = this.#items.get(this.#tail)
    this.#items.delete(this.#tail)
    this.#rebalance()
    return item
  }

  /** Removes and returns up to `count` elements from the back of the deque (in back-to-front order) */
  popBackMany(count: number): T[] {
    const result: T[] = []
    const actualCount = Math.min(count, this.size)
    for (let i = 0; i < actualCount; i++) {
      this.#tail--
      result.push(this.#items.get(this.#tail) as T)
      this.#items.delete(this.#tail)
    }
    this.#rebalance()
    return result
  }

  /** Returns the element at the front without removing it */
  peekFront(): T | undefined {
    if (this.isEmpty) {
      return undefined
    }
    return this.#items.get(this.#head)
  }

  /** Returns the element at the back without removing it */
  peekBack(): T | undefined {
    if (this.isEmpty) {
      return undefined
    }
    return this.#items.get(this.#tail - 1)
  }

  /** Returns the element at the given index (0-based from front) */
  at(index: number): T | undefined {
    if (index < 0 || index >= this.size) {
      return undefined
    }
    return this.#items.get(this.#head + index)
  }

  /** Removes all elements from the deque */
  clear(): void {
    this.#items.clear()
    this.#head = 0
    this.#tail = 0
  }

  /** Converts the deque to an array (front to back) */
  asArray(): T[] {
    const result: T[] = []
    for (let i = this.#head; i < this.#tail; i++) {
      result.push(this.#items.get(i) as T)
    }
    return result
  }

  /** Iterates over elements from front to back */
  *[Symbol.iterator](): Iterator<T> {
    for (let i = this.#head; i < this.#tail; i++) {
      yield this.#items.get(i) as T
    }
  }

  /** Iterates over elements from back to front */
  *reverse(): Iterable<T> {
    for (let i = this.#tail - 1; i >= this.#head; i--) {
      yield this.#items.get(i) as T
    }
  }

  /**
   * Rebalances the internal indices to prevent integer overflow
   * when head/tail grow too large in absolute value
   */
  #rebalance(): void {
    // Rebalance when indices get too far from zero to prevent potential issues
    const threshold = 1_000_000
    if (this.#head < -threshold || this.#tail > threshold) {
      const newItems = new Map<number, T>()
      let newIndex = 0
      for (let i = this.#head; i < this.#tail; i++) {
        newItems.set(newIndex, this.#items.get(i) as T)
        newIndex++
      }
      this.#items = newItems
      this.#head = 0
      this.#tail = newIndex
    }
  }
}
